import { useEffect, useState } from 'react';
import { motivoLegivel } from '@/services/erroLegivel';
import { useTranslation } from 'react-i18next';

import { Badge, Button } from '@/components/atoms';
import { formatarDataHora } from '@/i18n/formato';
import {
  useCreateCredential,
  useCredentials,
  useDeleteCredential,
  useRevealCredential,
  useRotateCredential,
} from '@/hooks/useConfig';
import { ApiError } from '@/services/api';
import type { Credential } from '@/types/config';

import { usePlatformAccess } from '@/services/platform';
import styles from './ConfigCredenciais.module.css';

const REVEAL_TTL_MS = 30_000;

export const PROVIDERS_CATALOG: { value: string; label: string }[] = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'google_ai', label: 'Google AI' },
  { value: 'perplexity', label: 'Perplexity' },
  { value: 'serp', label: 'SERP API' },
  { value: 'smtp', label: 'SMTP' },
  { value: 'twitter', label: 'Twitter / X' },
  { value: 'supabase', label: 'Supabase' },
  { value: 'freeastrology', label: 'FreeAstrology' },
  { value: 'n8n', label: 'N8N' },
  { value: 'dalle', label: 'DALL-E' },
  { value: 'glitchtip', label: 'GlitchTip (error tracking)' },
];

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
    return `${e.status} ${e.message}`;
  }
  return String(e);
}

export /**
 * F-72: estas rotas do backend são infra da CASA (require_staff responde 404
 * de propósito, para não revelar a rota). Sem este guarda, o tenant admin via
 * a tabela sempre vazia e criação falhando permanentemente quebrado + alarmes globais de "não encontrado".
 * Chave própria por cliente (BYOK) chega na F1 — até lá, a parede diz isso.
 */
function ConfigCredenciais() {
  const { isStaff, query } = usePlatformAccess();
  if (!query.isFetched) return null;
  if (!isStaff) {
    return (
      <section style={{ maxWidth: 560, margin: '48px auto', textAlign: 'center', opacity: 0.85 }}>
        <h1>{'Credenciais e Chaves de API'}</h1>
        <p style={{ marginTop: 12, lineHeight: 1.5 }}>
          {
            'O cofre de credenciais é a vista master da casa (B-34) — as chaves que atendem seu plano vivem aqui, operadas pelo staff. Chave própria por cliente (BYOK) chega na F1; até lá, acione o suporte.'
          }
        </p>
      </section>
    );
  }
  return <ConfigCredenciaisCorpo />;
}

function ConfigCredenciaisCorpo() {
  const { t } = useTranslation(['config', 'common']);
  const [providerFilter, setProviderFilter] = useState<string>('');
  const {
    data: credentials = [],
    isLoading,
    error: erroLista,
  } = useCredentials(providerFilter || undefined);

  const createMut = useCreateCredential();
  const rotateMut = useRotateCredential();
  const deleteMut = useDeleteCredential();
  const revealMut = useRevealCredential();

  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    provider: 'openai',
    slug: 'main',
    secret: '',
    notes: '',
  });

  const [rotating, setRotating] = useState<Credential | null>(null);
  const [rotateForm, setRotateForm] = useState({
    secret: '',
    reason: '',
  });

  const [reveals, setReveals] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);

  // Auto-remask
  useEffect(() => {
    if (Object.keys(reveals).length === 0) return;
    const timer = setTimeout(() => setReveals({}), REVEAL_TTL_MS);
    return () => clearTimeout(timer);
  }, [reveals]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    try {
      await createMut.mutateAsync({
        provider: createForm.provider,
        slug: createForm.slug || 'main',
        secret: createForm.secret || null,
        notes: createForm.notes || null,
      });
      setMsg(`Credencial ${createForm.provider} adicionada.`);
      setCreating(false);
      setCreateForm({
        provider: 'openai',
        slug: 'main',
        secret: '',
        notes: '',
      });
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  async function handleRotate(e: React.FormEvent) {
    e.preventDefault();
    if (!rotating) return;
    setMsg(null);
    try {
      await rotateMut.mutateAsync({
        id: rotating.id,
        payload: {
          secret: rotateForm.secret,
          reason: rotateForm.reason || null,
        },
      });
      setMsg(`Credencial ${rotating.provider}/${rotating.slug} rotada.`);
      setRotating(null);
      setRotateForm({ secret: '', reason: '' });
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  async function handleReveal(cred: Credential) {
    if (
      !window.confirm(
        `Vais ver o segredo desta credencial em texto plano.\n\n` +
          `Esta ação é registrada em audit log. Continuar?`,
      )
    ) {
      return;
    }
    try {
      const r = await revealMut.mutateAsync(cred.id);
      setReveals((prev) => ({ ...prev, [cred.id]: r.secret }));
      setMsg(`Segredo visível por 30s (audit logged).`);
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  async function handleDelete(cred: Credential) {
    if (
      !window.confirm(
        `Apagar credencial ${cred.provider}/${cred.slug}?\n\n` +
          `Adapters que dependem desta credencial vão cair em fallback .env (se houver).`,
      )
    ) {
      return;
    }
    try {
      await deleteMut.mutateAsync(cred.id);
      setMsg(`Credencial removida.`);
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>{t('config:credentials.title')}</h1>
          <p className={styles.subtitle}>
            Vista master de todas as credenciais armazenadas (Fernet-encrypted em PostgreSQL).
            Reveal e Rotate ficam audit-logged.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>+ Nova credencial</Button>
      </header>

      <div className={styles.filterRow}>
        <span className={styles.label}>Provider</span>
        <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)}>
          <option value="">Todos</option>
          {PROVIDERS_CATALOG.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      {msg && <p className={styles.flash}>{msg}</p>}
      {isLoading && <p className={styles.muted}>Carregando credenciais…</p>}
      {/* Erro de listagem NAO e cofre vazio. A tela mostrava "Sem credenciais"
          em cima de um 500 — o operador cadastrava tudo de novo por cima. */}
      {erroLista != null && (
        <p className={styles.muted}>
          Não foi possível listar as credenciais. {motivoLegivel(erroLista)}
        </p>
      )}

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Provider</th>
            <th>Slug</th>
            <th>Status</th>
            <th>Secret</th>
            <th>Last rotated</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {credentials.map((cred) => {
            const reveal = reveals[cred.id];
            return (
              <tr key={cred.id}>
                <td>
                  {PROVIDERS_CATALOG.find((p) => p.value === cred.provider)?.label ?? cred.provider}
                </td>
                <td className={styles.mono}>{cred.slug}</td>
                <td>
                  <Badge tone={cred.active ? 'ok' : 'neutral'} size="xs">
                    {cred.active ? 'ativo' : 'inactivo'}
                  </Badge>
                </td>
                <td className={styles.mono}>
                  {reveal ? (
                    <span className={styles.revealed}>{reveal}</span>
                  ) : (
                    (cred.secret_masked ?? '—')
                  )}
                </td>
                <td className={styles.mono}>
                  {cred.last_rotated_at ? formatarDataHora(cred.last_rotated_at) : '—'}
                </td>
                <td>
                  <div className={styles.rowActions}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleReveal(cred)}
                      disabled={Boolean(reveal)}
                    >
                      {reveal ? 'Visível 30s' : 'Reveal'}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setRotating(cred)}>
                      Rotate
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(cred)}>
                      Apagar
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
          {credentials.length === 0 && !isLoading && erroLista == null && (
            <tr>
              <td colSpan={6} className={styles.muted}>
                Sem credenciais. Use <strong>+ Nova credencial</strong>.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {creating && (
        <div className={styles.modalBackdrop} onClick={() => setCreating(false)}>
          <form
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleCreate}
          >
            <h2 className={styles.modalTitle}>Nova credencial</h2>

            <label className={styles.field}>
              <span>Provider *</span>
              <select
                value={createForm.provider}
                onChange={(e) => setCreateForm({ ...createForm, provider: e.target.value })}
              >
                {PROVIDERS_CATALOG.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>

            <label className={styles.field}>
              <span>Slug</span>
              <input
                value={createForm.slug}
                onChange={(e) => setCreateForm({ ...createForm, slug: e.target.value })}
                placeholder="main"
              />
            </label>

            <label className={styles.field}>
              <span>Secret (texto plano — será criptado)</span>
              <input
                type="password"
                value={createForm.secret}
                onChange={(e) => setCreateForm({ ...createForm, secret: e.target.value })}
                autoComplete="new-password"
              />
            </label>

            <label className={styles.field}>
              <span>Notas</span>
              <input
                value={createForm.notes}
                onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                placeholder="Conta principal, billing, etc."
              />
            </label>

            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
                Cancelar
              </Button>
              <Button type="submit" loading={createMut.isPending}>
                Criar
              </Button>
            </div>
          </form>
        </div>
      )}

      {rotating && (
        <div className={styles.modalBackdrop} onClick={() => setRotating(null)}>
          <form
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleRotate}
          >
            <h2 className={styles.modalTitle}>
              Rotar {rotating.provider} / {rotating.slug}
            </h2>

            <label className={styles.field}>
              <span>Novo secret *</span>
              <input
                type="password"
                required
                value={rotateForm.secret}
                onChange={(e) => setRotateForm({ ...rotateForm, secret: e.target.value })}
                autoComplete="new-password"
              />
            </label>

            <label className={styles.field}>
              <span>Razão (opcional, vai para audit log)</span>
              <input
                value={rotateForm.reason}
                onChange={(e) => setRotateForm({ ...rotateForm, reason: e.target.value })}
                placeholder="key exposed in logs"
              />
            </label>

            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={() => setRotating(null)}>
                Cancelar
              </Button>
              <Button type="submit" variant="danger" loading={rotateMut.isPending}>
                Confirmar rotação
              </Button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
